import Icon from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import "ckeditor5/ckeditor5-content.css";
import "ckeditor5/ckeditor5.css";
import { useEffect, useRef, useState } from "react";
import { BsFillSendFill, BsSave, BsTrash3Fill } from "react-icons/bs";
import { LuAlarmClock } from "react-icons/lu";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { RESOURCE_API } from "../../../api/apiConstant";
import FileHandlerWrapper from "../../../components/mail/FileHandlerWrapper";
import { GeneralModal } from "../../../components/modal/GeneralModal";
import { MailScheduleModal } from "../../../components/modal/mail/MailScheduleModal";
import { PreviewFileUploadModal } from "../../../components/modal/PreviewFileUploadModal";
import { MailFilesPreview } from "../../../components/preview/MailFilesPreview";
import EmailComposer from "../../../components/richTextEditor/EmailComposer";
import { conversationType } from "../../../config/Constant";
import { SSendIcon } from "../../../config/Icon";
import { useDraftMailContext } from "../../../context/DraftMailContext";
import { useMenuContext } from "../../../context/MenuContext";
import { useUnsavedChangesBlocker } from "../../../hooks/useUnsavedChangesBlocker";
import { useInfoUser } from "../../../store/UserStore";
import { formatShortTime } from "../../../utils/formatTime";
import {
  blobToBase64,
  extractEditorResourceIds,
  getFirstLineFromHtml,
  getPlainTextFromHtml,
  openNotificationError,
  openNotificationSuccess,
  openNotificationWarn,
} from "../../../utils/Utils";
import "./style.scss";

const MailComposer = ({ notification, addMemberOrg }) => {
  const [mailContent, setMailContent] = useState({
    content: "",
    oldContent: "",
  });
  const {
    selectedConversation,
    setSelectedConversation,
    handleUpdateConversationList,
    conversationList,
    handleChangeConversation,
    onBackToConversations,
  } = useMenuContext();
  const { title, setTitle, setIsLoading, isLoading, isNotification } =
    useDraftMailContext();
  const { user } = useInfoUser();
  const replyDivRef = useRef(null);
  const { languageMap } = useInfoUser();
  const [isDeleteMailDraft, setIsDeleteMailDraft] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isMailScheduleModal, setIsMailScheduleModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [schedule, setSchedule] = useState({
    time: "",
    triggerId: uuidv4(),
    isUpdateScheduleTime: false,
  });
  const originalTitleRef = useRef(title || "");

  const {
    isOpenModalNotifyChange,
    handleSaveAndLeave,
    handleDiscardAndLeave,
    handleUpdateScheduleAndLeave,
    handleCancelLeave,
    proceedWithSubmit,
    cancelSubmit,
  } = useUnsavedChangesBlocker({
    isDirty,
    onSave: async () => {
      await createMailDraft("FROM_MODAL_CONFIRM");
    },
    onUpdateSchedule: schedule?.time ? () => createMailDraft(false) : undefined,
  });

  const clearAllBeforeEvent = () => {
    setMailContent({ content: "", oldContent: "" });
    setPreviewFiles([]);
    setUploadFiles([]);
    setIsLoading(false);
  };

  /*
   * 1. create empty conversation
   * 2. send new letter to above conversation
   * */
  const createNotification = async () => {
    proceedWithSubmit();

    if (!validateSendMail(true)) {
      cancelSubmit();
      return;
    }

    setIsLoading(true);
    const conversationResponse =
      await apiFactory.conversationApi.createEmptyConversation({
        conversationName: title || getFirstLineFromHtml(mailContent?.content),
        isNotification: isNotification,
      });

    if (conversationResponse?.status !== 200)
      openNotificationError("Create conversation failed");

    const { userIds, ccIds } = addMemberOrg();

    const letter = await apiFactory.letterApi.send({
      name: title || getFirstLineFromHtml(mailContent?.content),
      recipientIds: userIds,
      ccIds,
      conversationType: conversationType.NOTIFICATION,
      conversationId: conversationResponse?.data?.conversationId,
      isNotification: isNotification,
      plainText: getPlainTextFromHtml(mailContent?.content),

      content: processHtmlForSaving(mailContent?.content),
      isDraft: false,
      files: previewFiles || [],
      updatedMediaConversationId: true,
      draftMailId: notification?.draftMail?.draftMailId,
      mailId: notification?.mailId,
      editorResourceId: extractEditorResourceIds(mailContent?.content),
      isForwardMail: false,
    });

    onCreateNotificationSuccessfully({
      conversationId: conversationResponse?.data?.conversationId,
    });
    clearAllBeforeEvent();
    setIsLoading(false);
  };

  const onCreateNotificationSuccessfully = (conversation) => {
    conversation.type = conversationType.NOTIFICATION;
    handleChangeConversation(conversation);
  };

  const validateSendMail = (isNoti) => {
    if (
      mailContent?.content?.trim()?.length === 0 &&
      previewFiles?.length === 0
    ) {
      if (isNoti) {
        openNotificationError(
          languageMap?.["error.mailHaveContent"] ??
            "You have to input email content",
        );
      }

      setIsLoading(false);
      return false;
    }

    return true;
  };

  const processHtmlForSaving = (htmlContent) => {
    if (!htmlContent) return "";

    const doc = new DOMParser().parseFromString(htmlContent, "text/html");
    const images = Array.from(doc.querySelectorAll("img"));

    images.forEach((imgDoc) => {
      const resourceId = imgDoc.getAttribute("data-resource-id");

      if (resourceId) {
        const apiUrl = `${process.env.REACT_APP_AUTHENTICATION_API_URL}/${RESOURCE_API}/free/file/${resourceId}`;

        imgDoc.src = apiUrl;

        imgDoc.removeAttribute("data-resource-id");
      }
    });

    return doc.body.innerHTML;
  };

  const isBase64 = (str) => {
    return str.startsWith("data:image/") && str.includes(";base64");
  };

  const handleDeleteMailDraft = async () => {
    const { conversationId, draftMail } = notification || {};
    const draftMailId = draftMail?.draftMailId;

    if (!conversationId || !draftMailId) {
      setIsDeleteMailDraft(false);
      return;
    }

    try {
      const rs = await apiFactory.draftMessageApi.delete({
        conversationId,
        draftMailId,
        type: conversationType.DRAFT,
      });

      if (rs?.status !== 200) {
        openNotificationError("Delete Mail Draft Fail!");
        return;
      }

      const newConvs = conversationList.filter(
        (c) => c?.conversationId !== selectedConversation?.conversationId,
      );

      handleUpdateConversationList(newConvs);

      onBackToConversations();

      openNotificationSuccess("Delete Mail Draft Successfully!");
    } catch (error) {
      openNotificationError("Delete Mail Draft Error!");
      console.error(error);
    } finally {
      setIsDeleteMailDraft(false);
    }
  };

  const validateDraftMail = () => {
    if (!mailContent?.content && previewFiles?.length === 0) {
      openNotificationWarn("Please enter content!");
      return false;
    }

    return true;
  };

  const createMailDraft = async (isDraft) => {
    if (!validateDraftMail()) return;

    if (!title) {
      setTitle(getFirstLineFromHtml(mailContent?.content));
    }

    const { userIds, ccIds } = addMemberOrg();

    const request = {
      conversationId: selectedConversation?.conversationId,
      recipientIds: userIds,
      ccIds: ccIds,
      scheduleTime: schedule?.time || null,
      triggerId: schedule?.triggerId || null,
      requestUuid: uuidv4(),
      title: title || getFirstLineFromHtml(mailContent?.content),
      content: mailContent?.content,
      files: previewFiles || [],
      isUpdateScheduleTime: isDraft ? false : schedule?.isUpdateScheduleTime,
    };

    let response;

    if (isDraft) {
      response = await apiFactory.draftMessageApi.createMailDraft(request);
    } else {
      if (userIds?.length === 0 && ccIds?.length === 0) {
        openNotificationError(
          "Please choose at least one recipient to schedule mail!",
        );
        return;
      }

      response = await apiFactory.draftMessageApi.schedule(request);
    }

    const { data, status } = response;

    if (status === 200) {
      const conversation = {
        conversationId: data?.conversationId,
        conversationStatus: "ACTIVE",
        creatorId: user?.userId,
        creatorName: user?.name,
        lastMessageTs: new Date(),
        pinnedConversationTs: data?.pinnedConversationTs,
        name: data?.conversationName,
        role: data?.role,
        hasFile: data?.hasFile,
        hasReply: data?.hasReply,
        starred: data?.starred,
        type: data?.type,
        unreadCount: data?.unreadCount,
        updatedAt: data?.updatedAt,
        userConversationId: data?.userConversationId,
        lastMessageAt: new Date(),
        scheduleAt: data?.scheduleAt,
      };

      handleUpdateConversationList(conversation);

      setSchedule((prev) => ({
        ...prev,
        time: isDraft ? "" : prev.time,
        isUpdateScheduleTime: false,
      }));

      setSelectedConversation(conversation);

      setIsDirty(false);

      openNotificationSuccess(
        selectedConversation?.conversationId
          ? "Update Mail Draft Successfully!"
          : "Create Mail Draft Successfully!",
      );
    } else {
      openNotificationError("Create Mail Draft Error!");
    }
  };

  const handleCancelUpload = () => {
    setIsOpenUploadModal(false);
    setUploadFiles([]);
  };

  useEffect(() => {
    const processReceivedImages = async () => {
      let content = notification?.content;
      if (!content) {
        return;
      }

      const doc = new DOMParser().parseFromString(content, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      const imagePromises = images.map(async (imgDoc) => {
        if (!imgDoc?.src) return Promise.resolve();

        if (isBase64(imgDoc.src)) return Promise.resolve();

        if (!imgDoc.src.includes(RESOURCE_API)) {
          return Promise.resolve();
        }

        const parts = imgDoc.src.split("/");
        const resourceId = parts.pop();
        if (!resourceId || resourceId === "null") {
          return Promise.resolve();
        }

        try {
          const fetchedBlob =
            await apiFactory.resourceApi.getStreamFileAsBlob(resourceId);

          if (
            !fetchedBlob ||
            fetchedBlob.size === 0 ||
            fetchedBlob.type.startsWith("application/json")
          ) {
            imgDoc.remove();
            return;
          }

          const base64Data = await blobToBase64(fetchedBlob);

          imgDoc.src = base64Data;
        } catch (error) {
          console.error("Failed to load received image:", resourceId, error);
          imgDoc.remove(); // Xóa nếu không tải được
        }
      });

      await Promise.all(imagePromises);
    };

    processReceivedImages();
  }, [notification?.content]);

  useEffect(() => {
    setPreviewFiles(
      selectedConversation?.conversationId
        ? notification?.draftMail?.files.map((file) => ({
            ...file,
            fileDraftOld: true,
          }))
        : [],
    );
    setUploadFiles([]);
  }, [selectedConversation?.conversationId, notification]);

  useEffect(() => {
    originalTitleRef.current = title || "";
  }, [title]);

  useEffect(() => {
    setIsDirty(
      mailContent?.content !== mailContent?.oldContent ||
        previewFiles?.some((f) => !f.fileDraftOld) ||
        (title ?? "") !== (originalTitleRef.current ?? ""),
    );
  }, [mailContent, title, previewFiles]);

  useEffect(() => {
    setSchedule({
      time: notification?.scheduleTime ? notification?.scheduleTime : "",
      triggerId: notification?.triggerId ? notification?.triggerId : uuidv4(),
      isUpdateScheduleTime: false,
    });
  }, [notification]);

  return (
    <div>
      <FileHandlerWrapper
        className="mt-2"
        setUploadFiles={setUploadFiles}
        setIsOpenUploadModal={setIsOpenUploadModal}
        languageMap={languageMap}
        innerRef={replyDivRef}
      >
        <EmailComposer
          originalContent={notification?.draftMail?.content}
          notification={notification?.draftMail}
          setMailContent={setMailContent}
          mailContent={mailContent}
          conversationId={notification?.conversationId}
        />
        <div className="flex flex-col">
          <div className="flex gap-[10px]">
            <MailFilesPreview
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              setIsOpenUploadModal={setIsOpenUploadModal}
              setUploadFiles={setUploadFiles}
            />
          </div>
          <div className="flex justify-between mt-[25px]">
            <div className="flex gap-[5px] items-center">
              <Tooltip
                placement="top"
                title={
                  languageMap?.["modal.conversation.schedule"] ?? "Schedule"
                }
                color={"#0091ff"}
              >
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  icon={<LuAlarmClock className="text-[18px]" />}
                  onClick={() => setIsMailScheduleModal(true)}
                />
              </Tooltip>
              <div className="font-semibold">
                {formatShortTime(schedule?.time)}
              </div>
              {schedule?.time && (
                <button
                  onClick={() =>
                    setSchedule({
                      ...schedule,
                      time: "",
                      isUpdateScheduleTime: true,
                    })
                  }
                >
                  <BsTrash3Fill className="text-[18px]" color="red" />
                </button>
              )}
            </div>

            <div className="flex justify-end gap-[5px]">
              <Tooltip placement="top" title={"Save draft"} color={"#0091ff"}>
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  onClick={() => createMailDraft(true)}
                  icon={<BsSave className="text-[18px]" />}
                />
              </Tooltip>
              {selectedConversation?.conversationId && (
                <Button
                  className="bg-[#2a56b9] text-white"
                  onClick={() => setIsDeleteMailDraft(true)}
                  icon={<BsTrash3Fill className="text-[18px]" color="red" />}
                />
              )}
              {schedule?.time ? (
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  onClick={() => createMailDraft(false)}
                  disabled={isLoading}
                  icon={<Icon component={SSendIcon} className="text-[25px] " />}
                >
                  {languageMap?.["chat.mail.btn.schedule_send"] ??
                    "Schedule send"}
                </Button>
              ) : (
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  onClick={createNotification}
                  disabled={isLoading}
                  icon={
                    <BsFillSendFill className="text-[18px]" color="#30a3e6" />
                  }
                >
                  {languageMap?.["chat.mail.btn.send"] ?? "Send"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </FileHandlerWrapper>

      {isMailScheduleModal && (
        <MailScheduleModal
          onCancel={() => {
            setIsMailScheduleModal(false);
          }}
          open={isMailScheduleModal}
          schedule={schedule}
          setSchedule={setSchedule}
        />
      )}
      {isOpenModalNotifyChange && (
        <GeneralModal
          title={languageMap?.["modal.draft.title"] ?? "Save draft?"}
          content={
            languageMap?.["modal.draft.content"] ??
            "You have unsaved changes. Do you want to save this as a draft before leaving?"
          }
          open={isOpenModalNotifyChange}
          onUpdateSchedule={handleUpdateScheduleAndLeave}
          onConfirm={handleSaveAndLeave}
          onDiscard={handleDiscardAndLeave}
          onCancel={handleCancelLeave}
          updateScheduleText="Update Schedule"
          confirmText="Save"
          discardText="Discard"
          cancelText="Cancel"
        />
      )}
      {isDeleteMailDraft && (
        <GeneralModal
          title={
            selectedConversation.type === conversationType.SCHEDULE
              ? "Delete Schedule"
              : "Delete Mail Draft"
          }
          content={
            selectedConversation.type === conversationType.SCHEDULE
              ? "Do you want delete schedule?"
              : "Do you want delete mail draft?"
          }
          open={isDeleteMailDraft}
          onConfirm={handleDeleteMailDraft}
          onCancel={() => setIsDeleteMailDraft(false)}
        />
      )}
      {isOpenUploadModal && (
        <PreviewFileUploadModal
          isModalOpen={isOpenUploadModal}
          handleCancel={handleCancelUpload}
          uploadFiles={uploadFiles}
          setUploadFiles={setUploadFiles}
          setPreviewFiles={setPreviewFiles}
        />
      )}
    </div>
  );
};
export { MailComposer };
